/**
 * WorkspaceTabStrip.js
 *
 * Premium swipeable tab strip of the workspace dashboard: one pill per workspace
 * (horizontal scroll, the active pill is kept in view), a trailing "+" pill, and
 * a long-press on a pill to manage it. Solid surfaces, brand orange for the
 * active tab, dark-mode aware, a light `select` haptic on every switch.
 *
 * @module components/workspaces/WorkspaceTabStrip
 */

import React, {useEffect, useRef} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import haptics from '../../helpers/haptics';

/**
 * @param {object} props
 * @param {Array<{id: string, name: string, icon: ?string}>} props.boards Workspaces in order.
 * @param {string} props.activeId Active workspace id.
 * @param {(id: string) => void} props.onSelect Tab pressed.
 * @param {(id: string) => void} props.onManage Tab long-pressed (or active tab pressed again).
 * @param {() => void} props.onAdd "+" pressed.
 * @param {boolean} [props.canAdd=true] False once the workspace cap is reached (hides "+").
 * @returns {JSX.Element} The strip.
 */
export default function WorkspaceTabStrip({boards, activeId, onSelect, onManage, onAdd, canAdd = true}) {
    const m = useManagementTheme();
    const {t} = useTranslation();
    const scrollRef = useRef(null);
    const layouts = useRef({});

    // Keep the active pill visible after a swipe / reorder.
    useEffect(() => {
        const frame = layouts.current[activeId];

        if (frame && scrollRef.current) {
            scrollRef.current.scrollTo({x: Math.max(0, frame.x - 24), animated: true});
        }
    }, [activeId, boards.length]);

    return (
        <View style={styles.wrap}>
            <ScrollView
                ref={scrollRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
            >
                {boards.map((board) => {
                    const active = board.id === activeId;

                    return (
                        <TouchableOpacity
                            key={board.id}
                            onLayout={(event) => {
                                layouts.current[board.id] = event.nativeEvent.layout;
                            }}
                            onPress={() => {
                                if (active) {
                                    haptics.select();
                                    onManage(board.id);
                                } else {
                                    haptics.select();
                                    onSelect(board.id);
                                }
                            }}
                            onLongPress={() => {
                                haptics.medium();
                                onManage(board.id);
                            }}
                            delayLongPress={350}
                            activeOpacity={0.85}
                            accessibilityRole="tab"
                            accessibilityState={{selected: active}}
                            accessibilityLabel={board.name}
                            style={[
                                styles.pill,
                                active
                                    ? {backgroundColor: m.colors.primary, borderColor: m.colors.primary}
                                    : {backgroundColor: m.colors.surface, borderColor: m.colors.border},
                            ]}
                        >
                            {!!board.icon && <Icon name={board.icon} size={15} color={active ? '#FFFFFF' : m.colors.subText} style={styles.icon} />}
                            <Text style={[styles.label, {color: active ? '#FFFFFF' : m.colors.text}]} numberOfLines={1}>{board.name}</Text>
                            {active && <Icon name="chevron-down" size={15} color="#FFFFFF" style={styles.chevron} />}
                        </TouchableOpacity>
                    );
                })}
                {canAdd && (
                    <TouchableOpacity
                        onPress={() => {
                            haptics.tap();
                            onAdd();
                        }}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        accessibilityLabel={t('wsd_add_workspace')}
                        style={[styles.pill, styles.addPill, {borderColor: m.colors.primary}]}
                    >
                        <Icon name="plus" size={18} color={m.colors.primary} />
                    </TouchableOpacity>
                )}
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {marginBottom: 12, marginHorizontal: -4},
    content: {paddingHorizontal: 4, gap: 8, alignItems: 'center'},
    pill: {flexDirection: 'row', alignItems: 'center', height: 40, borderRadius: 20, borderWidth: 1, paddingHorizontal: 14, maxWidth: 220},
    addPill: {width: 40, paddingHorizontal: 0, justifyContent: 'center', borderStyle: 'dashed', borderWidth: 1.5},
    icon: {marginRight: 6},
    label: {fontSize: 14, fontWeight: '700', flexShrink: 1},
    chevron: {marginLeft: 2},
});
